import type { IndustrialCaseSummary } from '@/domain/industrialCase/industrialCaseSummary';
import Link from '@/routing/AppLink';
import React from 'react';

import { IconArrowRightRound } from '@/components/apps/ui/icon';
import BlurImage from '@/components/shared/ui/BlurImage';

import theme from '@/styles/theme';

import { buildIndustrialCasePath } from './industrialCaseRoutes';

type IndustrialCaseCardProps = {
  caseItem: IndustrialCaseSummary;
  ctaLabel: string;
  targetSoundLabel: string;
  metricLabel: string;
  onClick: () => void;
};

const CASE_IMAGE_SIZES =
  '(min-width: 1400px) 310px, (min-width: 1200px) 22vw, (min-width: 768px) 45vw, calc(100vw - 40px)';

const CARD_BUTTON_CLASS =
  'flex overflow-hidden flex-col w-full h-full p-0 border border-[rgba(255,255,255,0.08)] rounded-[20px] bg-[rgba(255,255,255,0.04)] shadow-[0_8px_40px_rgba(0,0,0,0.2)] text-left backdrop-blur-[30px] [transition:border-color_180ms_ease,background-color_180ms_ease,transform_180ms_ease] hover:border-[rgba(255,255,255,0.24)] hover:bg-[rgba(255,255,255,0.07)] hover:[transform:translateY(-4px)] focus-visible:outline-2 focus-visible:outline-apps-corePrimary01 focus-visible:outline-offset-4';

const IMAGE_FRAME_CLASS =
  "relative w-full h-[152px] bg-apps-coreGray01 after:absolute after:right-0 after:bottom-0 after:left-0 after:h-[72px] after:bg-[linear-gradient(180deg,rgba(22,22,23,0)_0%,rgba(22,22,23,0.78)_100%)] after:content-['']";

const STYLED_IMAGE_CLASS = 'object-cover object-center';

const SOUND_TAG_CLASS =
  'absolute left-4 bottom-[14px] z-[1] max-w-[calc(100%-32px)] px-2.5 py-1.5 rounded-[999px] bg-[rgba(22,22,23,0.82)] text-apps-coreGrayWhite text-xs font-extrabold leading-none whitespace-nowrap';

const CARD_BODY_CLASS = 'flex flex-1 flex-col gap-[14px] p-6';

const META_CLASS =
  'text-apps-corePrimary03 text-[13px] font-bold leading-[1.45]';

const TITLE_CLASS =
  'text-apps-coreGrayWhite text-[22px] font-bold leading-[1.35]';

const SUMMARY_CLASS =
  'text-apps-brandGray700 text-[15px] font-medium leading-[1.6]';

const TARGET_SOUND_CLASS =
  'flex flex-wrap gap-2 items-center text-apps-brandGray700 text-[13px] leading-[1.45] [&_strong]:text-apps-coreGrayWhite [&_strong]:font-bold';

const METRIC_BLOCK_CLASS =
  'flex flex-col gap-1 p-4 rounded-xl bg-[rgba(255,255,255,0.07)]';

const METRIC_LABEL_CLASS =
  'text-apps-brandGray700 text-xs font-semibold leading-[1.4]';

const METRIC_VALUE_CLASS =
  'text-apps-coreGrayWhite text-2xl font-extrabold leading-[1.2]';

const METRIC_CAPTION_CLASS =
  'text-apps-brandGray600 text-[13px] leading-[1.45]';

const CTA_CLASS =
  'inline-flex gap-1.5 items-center mt-auto text-apps-coreGrayWhite text-sm font-bold leading-[1.4]';

/** Published customer case linked to its localized detail page. */
export function IndustrialCaseCard({
  caseItem,
  ctaLabel,
  targetSoundLabel,
  metricLabel,
  onClick,
}: IndustrialCaseCardProps) {
  return (
    <Link
      className={CARD_BUTTON_CLASS}
      href={buildIndustrialCasePath(caseItem.slug)}
      prefetch={false}
      onClick={onClick}
      aria-label={`${caseItem.title} ${ctaLabel}`}
    >
      {caseItem.heroImage && (
        <div className={IMAGE_FRAME_CLASS}>
          <BlurImage
            className={STYLED_IMAGE_CLASS}
            src={caseItem.heroImage}
            alt=""
            fill
            quality={82}
            sizes={CASE_IMAGE_SIZES}
          />
          <span className={SOUND_TAG_CLASS}>{caseItem.targetSound}</span>
        </div>
      )}
      <div className={CARD_BODY_CLASS}>
        <p className={META_CLASS}>
          {[caseItem.industry, caseItem.process, caseItem.inspectionTarget]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <h3 className={TITLE_CLASS}>{caseItem.title}</h3>
        <p className={SUMMARY_CLASS}>{caseItem.summary}</p>
        <p className={TARGET_SOUND_CLASS}>
          <span>{targetSoundLabel}</span>
          <strong>{caseItem.targetSound}</strong>
        </p>
        <div className={METRIC_BLOCK_CLASS}>
          <span className={METRIC_LABEL_CLASS}>{metricLabel}</span>
          <strong className={METRIC_VALUE_CLASS}>
            {caseItem.primaryMetric.value}
          </strong>
          <span className={METRIC_CAPTION_CLASS}>
            {caseItem.primaryMetric.label}
          </span>
        </div>
        <span className={CTA_CLASS}>
          <span>{ctaLabel}</span>
          <IconArrowRightRound
            size={12}
            color={theme`colors.apps.coreGrayWhite`}
            aria-hidden="true"
          />
        </span>
      </div>
    </Link>
  );
}
